
import { supabase } from '@/integrations/supabase/client';
import { validateFile } from './fileValidation';
import { generateVideoThumbnail } from './thumbnailGenerator';
import { uploadFile } from './uploadMedia';
import { toast } from 'sonner';
import { trackUploadFailure, trackNetworkError } from './upload-error-tracker';
import { compressImage } from './image-compression';

export interface MediaUploadProgress {
  progress: number;
  state: 'idle' | 'uploading' | 'success' | 'error';
}

interface MediaUploadResult {
  url: string;
  thumbnailUrl?: string;
  fileType: string;
  version: number;
}

export const handleMediaUpload = async (
  file: File,
  userId: string,
  isPremiumUser: boolean,
  onProgress?: (progress: MediaUploadProgress) => void,
  performModeration: boolean = true
): Promise<MediaUploadResult> => {
  try {
    console.log('Starting upload process:', {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      userId,
      bucket: 'bulletin-images',
      performModeration
    });
    
    if (!file || !userId) {
      console.error('Upload failed: Missing file or user ID', { file, userId });
      throw new Error('Missing required upload parameters');
    }

    onProgress?.({ progress: 0, state: 'uploading' });

    // Validate file, but only check mature content if performModeration is true
    const validationResult = performModeration 
      ? await validateFile(file, isPremiumUser)
      : { file, isMature: false }; // Skip content moderation if not performing it
      
    let validatedFile = validationResult.file;
    
    // Don't pass isMature flag from here - it will be determined by background moderation later
    
    const isVideo = validatedFile.type.startsWith('video/');
    
    console.log('File validated:', {
      name: validatedFile.name,
      type: validatedFile.type,
      size: validatedFile.size,
      moderationPerformed: performModeration
    });
    
    // Compress images to reduce upload time and storage (fallback to original if fails)
    if (validatedFile.type.startsWith('image/')) {
      try {
        const compressionResult = await compressImage(validatedFile, {
          maxSizeMB: 10, // Target 10MB after compression
          maxWidthOrHeight: 4096, // High quality 4K
          initialQuality: 0.85,
          onProgress: (progress) => {
            // Map compression progress to 0-25% of total progress
            const mappedProgress = Math.round(progress * 0.25);
            onProgress?.({ progress: mappedProgress, state: 'uploading' });
          }
        });
        
        if (compressionResult.wasCompressed) {
          validatedFile = compressionResult.file;
          console.log('Image compressed successfully:', {
            originalSize: (compressionResult.originalSize / 1024 / 1024).toFixed(2) + 'MB',
            compressedSize: (compressionResult.compressedSize / 1024 / 1024).toFixed(2) + 'MB',
            saved: compressionResult.compressionRatio + '%'
          });
        } else {
          console.log('Using original file (compression skipped or failed)');
        }
      } catch (compressionError) {
        console.warn('Compression failed, using original file:', compressionError);
        // Continue with original file - compression is optional
      }
    }
    
    // Final size check after compression
    const finalSizeMB = validatedFile.size / 1024 / 1024;
    if (finalSizeMB > 25) {
      console.warn('File still large after compression:', finalSizeMB.toFixed(2) + 'MB');
      // Still try to upload, storage will enforce its own limits
    }
    
    const version = Date.now();
    const { publicUrl } = await uploadFile({
      file: validatedFile,
      userId,
      path: `${version}-${validatedFile.name}`,
      contentType: validatedFile.type
    });

    onProgress?.({ progress: 75, state: 'uploading' });

    let thumbnailUrl;
    if (isVideo) {
      const thumbnailDataUrl = await generateVideoThumbnail(validatedFile);
      const thumbnailBlob = await fetch(thumbnailDataUrl).then(r => r.blob());
      const thumbnailFile = new File([thumbnailBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
      
      const { publicUrl: thumbUrl } = await uploadFile({
        file: thumbnailFile,
        userId,
        path: `${userId}/thumbnails/${version}_thumb.jpg`,
        contentType: 'image/jpeg'
      });

      thumbnailUrl = thumbUrl;
      console.log('Generated thumbnail URL:', thumbnailUrl);
    }

    onProgress?.({ progress: 100, state: 'success' });

    return {
      url: publicUrl,
      thumbnailUrl,
      fileType: validatedFile.type.split('/')[1],
      version
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    console.error('Media upload error:', {
      message: errorMessage,
      name: error instanceof Error ? error.name : 'Unknown',
      stack: error instanceof Error ? error.stack : undefined,
      fileSize: file?.size,
      fileType: file?.type
    });
    
    // Automatically track the error
    const isNetworkError = errorMessage.toLowerCase().includes('network') || 
                           errorMessage.toLowerCase().includes('timeout') ||
                           errorMessage.toLowerCase().includes('fetch');
    
    if (isNetworkError) {
      trackNetworkError(errorMessage, file, error instanceof Error ? error : undefined);
    } else {
      trackUploadFailure(errorMessage, file, error instanceof Error ? error : undefined, {
        bucket: 'bulletin-images'
      });
    }
    
    onProgress?.({ progress: 0, state: 'error' });
    toast.error(errorMessage || 'Failed to upload file');
    throw error;
  }
};
